import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Mail, Download, CheckCircle2 } from "lucide-react";

export default function Members() {
    const [data, setData] = useState({ count: 0, active: 0, items: [] });
    useEffect(() => { api.get("/members").then((r) => setData(r.data)).catch(() => {}); }, []);

    const exportCsv = () => downloadCSV(`members-${new Date().toISOString().slice(0, 10)}.csv`, data.items, [
        { key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "status", label: "Status" }, { key: "stripe_subscription_id", label: "Subscription" },
        { key: "since", label: "Member since" },
    ]);

    return (
        <div className="space-y-6" data-testid="members-page">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">Members</h1>
                    <p className="text-slate-500 mt-1">Paying EDI Connect members (Standard monthly or yearly, and Student plans).</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="gap-2" onClick={exportCsv} disabled={data.items.length === 0} data-testid="members-export-csv"><Download className="h-4 w-4" /> Export CSV</Button>
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm px-6 py-3 text-center" data-testid="members-total">
                        <p className="text-2xl font-extrabold text-nhs-ink">{data.active}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Active</p>
                    </div>
                </div>
            </div>

            {data.items.length === 0 ? (
                <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="members-empty">
                    <Users className="h-10 w-10 text-slate-300 mx-auto" />
                    <p className="text-slate-500 mt-4">No members yet. New paid members will appear here automatically.</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-left">
                            <tr>
                                <th className="px-5 py-3 font-semibold">Member</th>
                                <th className="px-5 py-3 font-semibold">Status</th>
                                <th className="px-5 py-3 font-semibold">Since</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {data.items.map((m, i) => (
                                <tr key={m.email + i} data-testid="member-row">
                                    <td className="px-5 py-4">
                                        <p className="font-semibold text-nhs-ink">{m.name || "—"}</p>
                                        <a href={`mailto:${m.email}`} className="text-slate-500 inline-flex items-center gap-1.5 hover:text-nhs-blue"><Mail className="h-3.5 w-3.5" /> {m.email}</a>
                                    </td>
                                    <td className="px-5 py-4">
                                        <Badge className={m.status === "active" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 hover:bg-slate-100"}>
                                            {m.status === "active" && <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}{m.status}
                                        </Badge>
                                    </td>
                                    <td className="px-5 py-4 text-slate-500">{m.since ? new Date(m.since).toLocaleDateString() : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
